import { useMutation, useQueryClient } from '@tanstack/react-query';
import PaymentsAPIServiceV2 from './PaymentsServiceV2';
import { PAYMENT_QUERY_KEYS } from './paymentsQuery';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useTranslation } from 'react-i18next';

const paymentsService = PaymentsAPIServiceV2.getInstance();

export const usePaymentsMutations = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useNotificationsSnackbar();
    const { t } = useTranslation();

    const updateCommonDataFlag = useMutation({
        mutationFn: paymentsService.updateCompanyDetails,
        onSuccess: () => {
            //TODO: get company to query
            setAlert(t('common_data_updated'), 'success');
        },
        onError: (error) => {
            setAlert(error.message, 'error');
        }
    });

    const updateCompanyDetails = useMutation({
        mutationFn: paymentsService.updateCompanyDetails,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getImportFilters]
            });
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getCurrentCompanyDetails]
            });
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const setupPlanday = useMutation({
        mutationFn: paymentsService.plandaySetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [
                    PAYMENT_QUERY_KEYS.PLANDAY_COMPANY_INFO,
                    PAYMENT_QUERY_KEYS.getAllIntegrationStatus
                ]
            });
        }
    });

    const setupTamigo = useMutation({
        mutationFn: paymentsService.tamigoSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.TAMIGO_COMPANY_INFO]
            });
        }
    });

    const setupTitanPlan = useMutation({
        mutationFn: paymentsService.titanPlanSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const updatePlandayChildCompany = useMutation({
        mutationFn: paymentsService.updatePlandayChildCompany,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.PLANDAY_COMPANY_INFO]
            });
        }
    });

    const setupSameSystem = useMutation({
        mutationFn: paymentsService.sameSystemSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                // queryKey: [PAYMENT_QUERY_KEYS.SAME_SYSTEM_COMPANY_INFO]
            });
        }
    });

    const setupDataLon = useMutation({
        mutationFn: paymentsService.dataLonSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const setupTrivec = useMutation({
        mutationFn: paymentsService.trivecSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const syncSyncBankTransactionRecords = useMutation({
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllBankTransactionReports]
            });
        },
        mutationFn: paymentsService.getSyncBankTransactionRecords
    });

    const ignoreBankTransactionRecord = useMutation({
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllBankTransactionReports]
            });
        },
        mutationFn: paymentsService.ignoreBankTransactionRecord
    });

    const topUpCompanyAccountWithTransaction = useMutation({
        mutationFn: paymentsService.topUpCompanyAccountWithTransaction,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllBankTransactionReports]
            });
        }
    });

    const cancelTransaction = useMutation({
        mutationFn: paymentsService.cancelTransaction,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllBankTransactionReports]
            });
        }
    });

    const importUsers = useMutation({
        mutationFn: paymentsService.importUsers,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getLast10Jobs]
            });
        }
    });

    const cancelJob = useMutation({
        mutationFn: paymentsService.cancelJob,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getLast10Jobs]
            });
        }
    });

    const setImportFilters = useMutation({
        mutationFn: paymentsService.setImportFilter,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getImportFilters]
            });
        }
    });

    const setImportFiltersForSource = useMutation({
        mutationFn: ({ sourceType, data }: { sourceType: string; data: any }) =>
            paymentsService.setImportFilterForSource(sourceType, data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getImportFilters]
            });
        }
    });

    const proloenSetup = useMutation({
        mutationFn: paymentsService.proloenSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                //queryKey: [PAYMENT_QUERY_KEYS.PROLOEN_COMPANY_S]
            });
        }
    });

    const eposSetup = useMutation({
        mutationFn: paymentsService.eposSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const staffSetup = useMutation({
        mutationFn: paymentsService.staffSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const lessorSetup = useMutation({
        mutationFn: paymentsService.lessorSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const quinyxSetup = useMutation({
        mutationFn: paymentsService.quinyxSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const dagrofaLonSetup = useMutation({
        mutationFn: paymentsService.dagrofaLonSetup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus]
            });
        }
    });

    const getAiiaAuthorizeUrl = useMutation({
        mutationFn: paymentsService.getAiiaAuthorizeUrl
    });

    const rejectTransactions = useMutation({
        mutationFn: paymentsService.rejectTransactions,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPendingTransactions]
            });
        }
    });

    const approveTransactions = useMutation({
        mutationFn: paymentsService.approveTransactions,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPendingTransactions]
            });
        }
    });

    const succeedTransactionInSalarySystem = useMutation({
        mutationFn: paymentsService.succeedTransactionInSalarySystem,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPendingTransactions]
            });
        }
    });

    const failTransactionInSalarySystem = useMutation({
        mutationFn: paymentsService.failTransactionInSalarySystem,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPendingTransactions]
            });
        }
    });

    const addPOSLocation = useMutation({
        mutationFn: paymentsService.addPOSLocation,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSLocationsAndDepartments]
            });
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSJournalLines]
            });
        }
    });
    const addTrivecLocation = useMutation({
        mutationFn: paymentsService.addTrivecLocation,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSLocationsAndDepartments]
            });
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSLocations]
            });
        }
    });
    const setPOSLocationDepartments = useMutation({
        mutationFn: paymentsService.setPOSLocationDepartments,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSLocationsAndDepartments]
            });
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSJournalLines]
            });
        }
    });

    const deletePOSLocation = useMutation({
        mutationFn: paymentsService.deletePOSLocation,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSLocationsAndDepartments]
            });
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSJournalLines]
            });
        }
    });

    const putEmployeeGroup = useMutation({
        mutationFn: paymentsService.putTipsEmployeeGroup,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSEmployeeGroup]
            });
        }
    });

    const putPOSJournalLines = useMutation({
        mutationFn: paymentsService.putPOSJournalLines,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSJournalLines]
            });
        }
    });

    const syncPOSJournalLineFilters = useMutation({
        mutationFn: paymentsService.syncPOSJournalLinesFilters,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSJournalLines]
            });
        }
    });

    const syncTrivecJournals = useMutation({
        mutationFn: paymentsService.syncTrivecJournals,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSJournalLines]
            });
        }
    });

    const balanceTextSummaryReset = useMutation({
        mutationFn: paymentsService.balanceTextSummaryReset,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getCurrentCompanyDetails]
            });
        }
    });

    const createEmploymentGroupTemplate = useMutation({
        mutationFn: paymentsService.createEmploymentGroupTemplate,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getEmploymentGroupTemplates]
            });
        }
    });

    const deleteEmploymentGroupTemplate = useMutation({
        mutationFn: paymentsService.deleteEmploymentGroupTemplate,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getEmploymentGroupTemplates]
            });
        }
    });

    const setEmployeeGroupTemplate = useMutation({
        mutationFn: paymentsService.setEmployeeGroupTemplate,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getPOSLocationsAndDepartments]
            });
        }
    });

    const updateEmployeeGroupTemplate = useMutation({
        mutationFn: paymentsService.updateEmployeeGroupTemplate,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getEmploymentGroupTemplates]
            });
        }
    });

    const updateJournalAmount = useMutation({
        mutationFn: paymentsService.updateJournalAmount,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getTipsHistory]
            });
        }
    });
    const documentUpload = useMutation({
        mutationFn: paymentsService.documentUpload,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getEmployeeDocuments]
            });
        }
    });

    const deleteDocument = useMutation({
        mutationFn: paymentsService.deleteDocument,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getEmployeeDocuments]
            });
        }
    });

    const createDocument = useMutation({
        mutationFn: paymentsService.createDocument,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getEmployeeDocuments]
            });
        }
    });

    const updateDocument = useMutation({
        mutationFn: ({ documentId, data }: { documentId: string; data: any }) =>
            paymentsService.updateDocument(documentId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_QUERY_KEYS.getEmployeeDocuments]
            });
        }
    });

    return {
        setupPlanday,
        updatePlandayChildCompany,
        updateCommonDataFlag,
        updateCompanyDetails,
        setupTamigo,
        setupDataLon,
        setupTrivec,
        setupSameSystem,
        setupTitanPlan,
        syncSyncBankTransactionRecords,
        ignoreBankTransactionRecord,
        topUpCompanyAccountWithTransaction,
        cancelTransaction,
        importUsers,
        cancelJob,
        proloenSetup,
        setImportFilters,
        setImportFiltersForSource,
        getAiiaAuthorizeUrl,
        rejectTransactions,
        approveTransactions,
        succeedTransactionInSalarySystem,
        failTransactionInSalarySystem,
        addPOSLocation,
        addTrivecLocation,
        setPOSLocationDepartments,
        deletePOSLocation,
        putEmployeeGroup,
        putPOSJournalLines,
        eposSetup,
        staffSetup,
        lessorSetup,
        quinyxSetup,
        dagrofaLonSetup,
        balanceTextSummaryReset,
        createEmploymentGroupTemplate,
        deleteEmploymentGroupTemplate,
        setEmployeeGroupTemplate,
        updateEmployeeGroupTemplate,
        syncPOSJournalLineFilters,
        syncTrivecJournals,
        updateJournalAmount,
        documentUpload,
        deleteDocument,
        createDocument,
        updateDocument
    };
};
